import { Link, useParams, useSearch } from '@tanstack/react-router';
import { PublicVerdict } from '../components/PublicVerdict.tsx';
import { useAuth } from '../auth/useAuth.ts';

/** Same bounds `SquadInput.gameweek` enforces in `@fantasy/types`, kept as
 * local constants — same reasoning `share.ts`'s own `GAMEWEEK_MIN`/
 * `GAMEWEEK_MAX` gives: `?gw=` is read as a plain search param here, not
 * parsed against a schema. */
const GAMEWEEK_MIN = 1;
const GAMEWEEK_MAX = 38;

/** `share.ts`'s unfurl target sets `?gw=` on the target URL (T-089) so a
 * shared link stays pinned to the gameweek it was made for. TanStack
 * Router's default search parser (see `router.tsx`: no `validateSearch` is
 * registered for this route) already turns a numeric-looking value into a
 * `number`, but a hand-edited or malformed URL can still hand this a string,
 * `NaN`, or a gameweek FPL never had — every one of those falls back to
 * `undefined`, i.e. "no override", the same as the param being absent. */
function parseGameweekParam(raw: unknown): number | undefined {
  const gameweek = Number(raw);
  if (!Number.isInteger(gameweek) || gameweek < GAMEWEEK_MIN || gameweek > GAMEWEEK_MAX) return undefined;
  return gameweek;
}

/**
 * `/try/$entryId` (T-088) — the free front door: a public FPL entry id in
 * the URL, resolved to a squad and its assessment with no account and no
 * write anywhere server-side. `App.tsx` decides the shell around this route
 * (the public masthead, signed out) or wraps it in the real `AppShell`
 * (signed in) — this component only decides what to render inside either.
 *
 * `strict: false` reads `entryId` off whichever route matched without this
 * file depending on `router.tsx`'s route object — the same reason
 * `EntryIdImport` and `LinkFplTeam` parse a raw string themselves rather
 * than trusting the router or a form library to have already done it.
 */
export function TryRoute() {
  const { status } = useAuth();
  const { entryId: entryIdParam } = useParams({ strict: false });
  // `strict: false` for the same reason `useParams` above is: this route has
  // no `validateSearch` registered in `router.tsx`, so this reads whatever
  // search params happen to be on the URL without depending on that file.
  const { gw } = useSearch({ strict: false }) as { gw?: unknown };
  const gameweek = parseGameweekParam(gw);

  // A manager who already has an account doesn't need the public read — the
  // real thing (their own linked squad, live rank, saved history) is one
  // click away, not a stripped-down copy of it on this page.
  if (status === 'authenticated') {
    return (
      <section className="panel panel--accent rise-in" aria-labelledby="try-signed-in-heading">
        <h3 id="try-signed-in-heading">You're already signed in</h3>
        <p className="hint">
          This public link is for visitors without an account — you have the real thing. Head to
          your squad desk to see it linked, saved and tracked.
        </p>
        <Link to="/squad" className="ghost small">
          Go to my squad desk
        </Link>
      </section>
    );
  }

  const entryId = Number(entryIdParam);
  if (!Number.isInteger(entryId) || entryId <= 0) {
    return (
      <section className="panel rise-in" aria-labelledby="try-invalid-heading">
        <h3 id="try-invalid-heading">That doesn't look like an FPL team id</h3>
        <p className="hint">It should be a whole number — check the link and try again.</p>
      </section>
    );
  }

  return <PublicVerdict entryId={entryId} gameweek={gameweek} />;
}
